import { calendarDaysBetween } from '../../shared/dates';
import type { LeadStatus, LeadWithNames } from '../../shared/types';

export function canLockInLead(lead: Pick<LeadWithNames, 'project_id'>): boolean {
  return lead.project_id === null;
}

export type LeadStageAction =
  | { kind: 'convert' }
  | { kind: 'postpone' }
  | { kind: 'lock' }
  | { kind: 'patch'; patch: { status: LeadStatus; closed_date?: string } };

/** The caller supplies Melbourne today. Choosing Won opens the convert flow
    (which marks the lead won) — except on a lead that already has a project,
    where converting again would 409, so it becomes a plain status patch. */
export function routeLeadStage(status: LeadStatus, today: string, opts: { converted?: boolean } = {}): LeadStageAction {
  if (status === 'won') return opts.converted ? { kind: 'patch', patch: { status } } : { kind: 'convert' };
  if (status === 'postponed') return { kind: 'postpone' };
  // Locked in asks for its start date first, then saves both together.
  if (status === 'locked_in') return { kind: 'lock' };
  return { kind: 'patch', patch: status === 'lost' ? { status, closed_date: today } : { status } };
}

const shortDate = (iso: string, weekday = false) => new Intl.DateTimeFormat('en-AU', {
  timeZone: 'UTC', ...(weekday ? { weekday: 'short' as const } : {}), day: 'numeric', month: 'short',
}).format(new Date(`${iso}T00:00:00Z`)).replace(/Sept\b/, 'Sep').replace(',', '');

/** A Locked-in lead's start date (stored in `resurface_date`): "Starts today",
    "Started 12 Oct" or "Starts 12 Jan". `due` = on or after the start date,
    when the Convert to project nudge shows. */
export function lockedInStart(start: string | null, today: string): { text: string; due: boolean } {
  if (!start) return { text: 'No start date', due: false };
  if (start === today) return { text: 'Starts today', due: true };
  if (start < today) return { text: `Started ${shortDate(start)}`, due: true };
  return { text: `Starts ${shortDate(start)}`, due: false };
}

export function leadFollowupMeta(lead: { status: LeadStatus; followup_date: string | null; resurface_date: string | null }, today: string): { text: string; tone: 'danger' | 'gold' | 'ok' | undefined } {
  const date = shortDate;
  if (lead.status === 'locked_in') {
    const start = lockedInStart(lead.resurface_date, today);
    return { text: start.text.charAt(0).toLowerCase() + start.text.slice(1), tone: start.due ? 'ok' : lead.resurface_date ? undefined : 'gold' };
  }
  if (lead.status === 'postponed') return { text: lead.resurface_date ? `resurfaces ${date(lead.resurface_date)}` : 'no resurface date', tone: lead.resurface_date ? undefined : 'gold' };
  if (!lead.followup_date) return { text: 'no follow-up', tone: 'gold' };
  if (lead.followup_date < today) return { text: `${calendarDaysBetween(lead.followup_date, today)}d overdue`, tone: 'danger' };
  return { text: `f/u ${date(lead.followup_date, true)}`, tone: undefined };
}
